import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';

// Prime React Components
import { classNames } from 'primereact/utils';
import { FilterMatchMode } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { FileUpload } from 'primereact/fileupload';

// Needed URLS
import { GET_NEURELIS_EVENTS, ADD_NEURELIS_EVENT, EDIT_NEURELIS_EVENT, DELETE_NEURELIS_EVENT, NEURELIS_IMAGE_PATH} from '../constants/URLS';
const GET_ENTITY = GET_NEURELIS_EVENTS
const ADD_ENTITY = ADD_NEURELIS_EVENT
const EDIT_ENTITY = EDIT_NEURELIS_EVENT
const DELETE_ENTITY = DELETE_NEURELIS_EVENT

// Needed Components
const formData = new FormData();

const Events = (props) => {

	// PAGE REFS
	const toast = useRef(null);
    const dt = useRef(null);
	const fileUploadRef = useRef(null);

	// CONSTANTS
    const emptyEntity = {
        eventID: null,
        title: '',
		description: '',
		startDate: '',
		endDate: '',
		location: '',
		photo: '', 
		isActive: 1
    };


	// STATE VARIABLES
    const [ entities, setEntities ] = useState([]); // holds all the existing 
	const [ entity, setEntity ] = useState(emptyEntity); // current entity that we are adding or editing
	const [ entityDialog, setEntityDialog ] = useState(false);
    const [ deleteEntityDialog, setDeleteEntityDialog ] = useState(false);
    const [ submitted, setSubmitted ] = useState(false);
    const [ globalFilterValue1, setGlobalFilterValue1 ] = useState('');
	const [ filters, setFilters ] = useState({
        'global': { value: null, matchMode: FilterMatchMode.CONTAINS }
		
    });

	// USE EFFECTS HOOKS
    useEffect(() => {
		fncGet()
    }, []); 

	// ACTION / XHR FUNCTIONS
	const fncGet = async () => {
		let strURL = GET_ENTITY
		let entityXHR = await axios({method: 'get', url: strURL})
		// need to do some slight data modifications for the form

		if (entityXHR.data.success) {
			setEntities(entityXHR.data.results.map(cur => {
				return {
					...cur,
					startDate: new Date(cur.startDate),
					endDate: new Date(cur.endDate)
				}
			}))
		} else {
			toast.current.show({ severity: 'error', summary: 'Error', detail: entityXHR.data.message, life: 3000 });
		}
	};

	const fncSave = async () => {
        setSubmitted(true);
		let errors = []
		if (!entity.title.trim()) errors.push('title')
		

		if (errors.length > 0) {
			return  toast.current.show({ severity: 'error', summary: 'Error', detail: 'Please fill in all required fields.', life: 3000 });
		}

		
		let _entity = {...entity};
		const formData = new FormData();
		formData.append("title", _entity.title);
		formData.append("description", _entity.description);
		formData.append("startDate", _entity.startDate);
		formData.append("endDate", _entity.endDate);
		formData.append("location", _entity.location);
		formData.append("isActive", _entity.isActive);

		let objCurUploader = fileUploadRef.current
		let arrFiles = objCurUploader.getFiles() || []
		if (arrFiles[0]) {
			formData.append("file", arrFiles[0]);
		}

		if (_entity.eventID) {
			formData.append("eventID", _entity.eventID);
			let editResults = await axios({
				method: 'post', 
				url: EDIT_ENTITY,
				data: formData,
				headers: { "Content-Type": "multipart/form-data" }
			})

			if (editResults.data.success) {
				await fncGet()
				toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Event Edited', life: 3000 });
			} else {
				toast.current.show({ severity: 'error', summary: 'Error', detail: editResults.data.message, life: 3000 });
			}
		} else {
			
			let addResults = await axios({
				method: 'post', 
				url: ADD_ENTITY,
				data: formData,
				headers: { "Content-Type": "multipart/form-data" }
			})

			if (addResults.data.success) {
				await fncGet()
				toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Event Created', life: 3000 });
			} else {
				toast.current.show({ severity: 'error', summary: 'Error', detail: addResults.data.message, life: 3000 });
			}
		}

		setSubmitted(false);
		setEntityDialog(false);
		clearEntity()
	}

	const fncDelete = async () => { 
		try {
			let deleteResults = await axios({
				method: 'post', 
				url: DELETE_ENTITY,
				data: {...entity}
			})
			if (deleteResults.data.success) {
				let _entities = entities.filter(val => val.eventID !== entity.eventID);
				setEntities(_entities);
				setDeleteEntityDialog(false);
				clearEntity()
				toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Event Deleted', life: 3000 });
			} else {
				toast.current.show({ severity: 'error', summary: 'Error', detail: deleteResults.data.message, life: 3000 });
			}
		} catch (err) {
			toast.current.show({ severity: 'error', summary: 'Error', detail: 'There was an error deleting.', life: 3000 });
			console.error(err)
		}
	}
	
	
	// OTHER FUNCTIONS
	const clearEntity = () => {
		setEntity(emptyEntity); 
	}
	
	const openNew = () => {
		clearEntity()
		setSubmitted(false);
		setEntityDialog(true);
	}

	const hideDialog = () => {
        setSubmitted(false);
        setEntityDialog(false);
    }

	const onGlobalFilterChange1 = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilterValue1(value);
    }

	const hideDeleteEntityDialog = () => {
        setDeleteEntityDialog(false);
    }

	const onInputChange = (value, name) => {
        let _entity = {...entity};
        _entity[`${name}`] = value;
        setEntity(_entity);
    }

	const fncEdit = (entity) => {
		setEntity(entity);
        setEntityDialog(true);
    }

	const confirmDeleteEntity = (entity) => {
        setEntity(entity);
        setDeleteEntityDialog(true);
    }

	// TABLE COLUMN TEMPLATES (table provides rowdata and column info, can draw how needed)
	const actionBodyTemplate = (rowData) => {
        return (
            <>
                <Button icon="pi pi-pencil" className="p-button-rounded p-button-success mr-2" onClick={() => fncEdit(rowData)} />
                <Button icon="pi pi-trash" className="p-button-rounded p-button-warning" onClick={() => confirmDeleteEntity(rowData)} />
            </>
        );
    }

	const dateTemplate = (rowData, column) => {
        return (
			<div className="flex flex-column">
				<div >
					{dayjs(rowData.timeStamp).format('MMM D, YYYY')}
				</div>
			</div>
		);
    }

	const eventDateTemplate = (rowData, column) => {
        return (
			<div className="flex flex-column">
				<div >
					{dayjs(rowData.startDate).format('MMM D, YYYY')} - {dayjs(rowData.endDate).format('MMM D, YYYY')}
				</div>
			</div>
		);
    }

	const photoTemplate = (rowData) => {
		return (
			<div className="flex eventCard">
				
				<div className="flex flex-column">
					<div className="flex ">
						<img src={NEURELIS_IMAGE_PATH + "events/" + rowData.photo} />
					</div>
				</div>
			</div>
		)
	}

	

	const detailsTemplate = (rowData) => {
		return (
			<div className="flex eventCard">
				
				<div className="flex flex-column">
					<div className="title">
						{rowData.title}
					</div>
					<div >
						{rowData.location} {rowData.description}
					</div>
					
					
				</div>
			</div>
		)
	}

	const activeTemplate = (rowData) => {
       if (rowData.isActive ===1 ) return <div className="green">Active</div>
	   else return <div className="red">Inactive</div>
    }

	

	// OTHER TEMPLATES (JSX)
	const leftToolbarTemplate = () => {
        return <Button label="Add New Event" icon="pi pi-plus" className="p-button-success mr-2" onClick={openNew} />
    }

	const rightToolbarTemplate = () => {
        return null
    }
	
	const header = (
        <div className="table-header flex justify-content-between" >
            <h5 className="mx-0 my-1" >Manage Events</h5>
			<div className="flex flex-column">
				<span className="p-input-icon-left">
					<i className="pi pi-search" />
					<InputText value={globalFilterValue1} onChange={onGlobalFilterChange1} placeholder="Search..." />
				</span>
			</div>
        </div>
    );

	const entityDialogFooter = (
        <>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label="Save" icon="pi pi-check" className="p-button-text" onClick={fncSave} />
        </>
    );

	

    const deleteEntityDialogFooter = (
        <>
            <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteEntityDialog} />
            <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={fncDelete} />
        </>
    );


	const fileHeaderTemplate = (options) => {
        const { className, chooseButton } = options;
        return (
            <div className={className} style={{ backgroundColor: 'transparent', display: 'flex', alignItems: 'center' }}>
                {chooseButton}
            </div>
        );
    };

	console.log(entity)
    

    return (
        <div className="datatable-resources">
             <Toast ref={toast} />

			<div className="card" style={{width:"100%"}}>
				<Toolbar className="mb-4" left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>

				<DataTable ref={dt} value={entities} sortField="startDate" sortOrder={-1}  
					dataKey="eventID" paginator rows={10} rowsPerPageOptions={[5, 10, 25]}
					paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
					currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Events"
					globalFilterFields={['title', 'photo', 'location', 'description']}
					filters={filters} header={header} responsiveLayout="stack">
						<Column field="startDate" body={eventDateTemplate} header="Event Date" sortable style={{ width: '15%', verticalAlign:'top' }}></Column>
						<Column field="isActive" sortable body={activeTemplate} header="Active" style={{ width: '10%', verticalAlign:'top' }}></Column>
						<Column field="title" header="Details" body={detailsTemplate} style={{ width: '45%', verticalAlign:'top' }}></Column>
						<Column field="photo" header="Photo" body={photoTemplate} style={{ width: '20%', verticalAlign:'top' }}></Column>
						<Column body={actionBodyTemplate} exportable={false} style={{ width: '10%', verticalAlign:'top' }}></Column>
				</DataTable>
            </div>

			<Dialog visible={entityDialog} style={{ width: '850px' }} header="Event Details" modal className="p-fluid" footer={entityDialogFooter} onHide={hideDialog}>
                <div className="flex flex-column inputForm">
					<div className="inputFormFields">
						<div className="flex">
							<div className="field fullField" >
								<label htmlFor="title">Event Title</label>
								<InputText id="title" value={entity.title} onChange={(e) => onInputChange(e.target.value, 'title')} required className={classNames({ 'p-invalid': submitted && !entity.title })} />
								{submitted && !entity.title && <small className="p-error">Title is required.</small>}
							</div>
						</div>

						<div className="flex justify-content-between">
							<div className="field pr-4 twoThirdField"  >
								<label htmlFor="description">Abbreviation</label>
								<InputText id="description" value={entity.description} onChange={(e) => onInputChange(e.target.value, 'description')} />
							</div>
							<div className="field oneThirdField">
								<label htmlFor="isActive">Active</label>
								<Dropdown value={entity.isActive} onChange={(e) => onInputChange(e.value,'isActive')} options={[{label: 'Active', value: 1},{label: 'Inactive',value: 0}]}  
									placeholder="Select a Status" className="w-full md:w-14rem" />
							</div>
						</div>
						
						<div className="flex justify-content-between">
							<div className="field pr-4 halfField">
								<label htmlFor="name">Start Date</label>
								<Calendar id="basic" value={entity.startDate} onChange={(e) => onInputChange(e.target.value,'startDate')} className={classNames({ 'p-invalid': submitted && !entity.startDate })}/>
								{submitted && !entity.startDate && <small className="p-error">Start Date is required.</small>}
							</div>
							<div className="field pr-4 halfField">
								<label htmlFor="name">End Date</label>
								<Calendar id="basic" value={entity.endDate} onChange={(e) => onInputChange(e.target.value,'endDate')} className={classNames({ 'p-invalid': submitted && !entity.endDate })}/>
							</div>
						</div>

						<div className="flex">
							<div className="field fullField" >
								<label htmlFor="location">Location</label>
								<InputText id="location" value={entity.location} onChange={(e) => onInputChange(e.target.value, 'location')} />
							</div>
						</div>
						
						<div className="flex">
							<div className="field fullField"  >
								<div className="flex justify-content-between" >
									<label htmlFor="photo">Background Photo (352 x 180) </label>
									{entity.eventID > 0 && 
										<div className="eventMessage">
											Leave blank to keep current image
										</div>
									}
								</div>
								<FileUpload 
									name="photo" 
									ref={fileUploadRef} 
									accept="image/*" 
									maxFileSize={1000000} 
									headerTemplate={fileHeaderTemplate}
									emptyTemplate={<p className="m-0">Drag and drop files to here to upload.</p>} />
								
							</div>
						</div>
					</div>
				</div>
            </Dialog>
			<Dialog visible={deleteEntityDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteEntityDialogFooter} onHide={hideDeleteEntityDialog}>
                <div className="confirmation-content">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem'}} />
                    {entity && <span>Are you sure you want to delete <b>{entity.title}</b>?</span>}
                </div>
            </Dialog>
        </div>
    );
}

export default Events;